import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity,
  Users,
  Upload,
  BookOpen,
  Clock,
  TrendingUp,
  RefreshCw,
  Loader2,
  ChevronDown,
  BarChart3,
  Timer,
  Target,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../App';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const API = '/api';

// Format seconds to readable time
const formatDuration = (seconds) => {
  if (!seconds) return 'N/A';
  
  const hours = Math.floor(seconds / 3600);
  const days = Math.floor(hours / 24);
  
  if (days >= 1) {
    return `${days}d ${hours % 24}h`;
  }
  if (hours >= 1) {
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  }
  const mins = Math.floor(seconds / 60);
  return `${mins}m`;
};

// Format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined) return '0%';
  return `${(value * 100).toFixed(1)}%`;
};

// Simple line chart component
const TrendChart = ({ data, dataKey1, dataKey2, label1, label2 }) => {
  if (!data || data.length === 0) return null;
  
  const max1 = Math.max(...data.map(d => d[dataKey1] || 0), 1);
  const max2 = Math.max(...data.map(d => d[dataKey2] || 0), 1);
  const maxVal = Math.max(max1, max2);
  
  return (
    <div className="mt-4">
      <div className="flex items-center gap-4 mb-2 text-xs text-zinc-500">
        <span className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-blue-500 rounded"></div>
          {label1}
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-green-500 rounded"></div>
          {label2}
        </span>
      </div>
      <div className="relative h-32 flex items-end gap-0.5">
        {data.map((point, idx) => (
          <div key={idx} className="flex-1 flex flex-col items-center gap-0.5 h-full justify-end group relative">
            {/* Signups bar */}
            <div 
              className="w-full bg-blue-500/60 rounded-t"
              style={{ height: `${(point[dataKey1] / maxVal) * 100}%`, minHeight: point[dataKey1] > 0 ? '4px' : '0' }}
            />
            {/* Activations bar */}
            <div 
              className="w-full bg-green-500 rounded-t"
              style={{ height: `${(point[dataKey2] / maxVal) * 100}%`, minHeight: point[dataKey2] > 0 ? '4px' : '0' }}
            />
            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-10">
              <div className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs whitespace-nowrap">
                <div className="font-medium text-white">{point.date}</div>
                <div className="text-blue-400">Signups: {point[dataKey1]}</div>
                <div className="text-green-400">Activations: {point[dataKey2]}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* X-axis labels */}
      <div className="flex mt-1 text-[10px] text-zinc-600">
        <span>{data[0]?.date?.slice(5)}</span>
        <span className="flex-1"></span>
        <span>{data[data.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
};

// Histogram bar chart
const HistogramChart = ({ buckets }) => {
  if (!buckets || buckets.length === 0) return null;
  
  const maxCount = Math.max(...buckets.map(b => b.count), 1);
  
  return (
    <div className="space-y-2">
      {buckets.map((bucket, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <span className="text-xs text-zinc-500 w-24 text-right">{bucket.label}</span>
          <div className="flex-1 h-6 bg-zinc-800 rounded overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded transition-all duration-500"
              style={{ width: `${(bucket.count / maxCount) * 100}%` }}
            />
          </div>
          <span className="text-xs text-zinc-400 w-16">
            {bucket.count} ({bucket.percentage}%)
          </span>
        </div>
      ))}
    </div>
  );
};

const AdminCreatorActivation = () => {
  useDocumentTitle('Creator Activation - Admin');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin && user.role !== 'admin') {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [user, navigate]);

  // Fetch metrics
  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/metrics/activation?time_range=${timeRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching activation metrics:', error);
      toast.error('Failed to load activation metrics');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  if (loading && !metrics) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-6" data-testid="admin-activation-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Activity className="w-6 h-6 text-orange-400" />
              Creator Activation
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Track user journey from signup to first publish
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32 bg-zinc-900 border-zinc-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="sm"
              onClick={fetchMetrics}
              disabled={loading}
              className="border-zinc-700"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Signups */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-xs text-zinc-500 uppercase">Signups</span>
              </div>
              <div className="text-3xl font-bold text-white">
                {metrics?.signups || 0}
              </div>
              <p className="text-sm text-zinc-500 mt-1">New users in period</p>
            </CardContent>
          </Card>

          {/* First Upload */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Upload className="w-5 h-5 text-purple-400" />
                <span className="text-xs text-zinc-500 uppercase">First Upload</span>
              </div>
              <div className="text-3xl font-bold text-white">
                {metrics?.users_with_first_upload || 0}
              </div>
              <p className="text-sm text-zinc-500 mt-1">
                {formatPercent(metrics?.upload_rate)} upload rate
              </p>
            </CardContent>
          </Card>

          {/* Activated Creators */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <BookOpen className="w-5 h-5 text-green-400" />
                <span className="text-xs text-zinc-500 uppercase">Activated</span>
              </div>
              <div className="text-3xl font-bold text-white">
                {metrics?.activated_creators || 0}
              </div>
              <p className="text-sm text-zinc-500 mt-1">
                {formatPercent(metrics?.activation_rate)} activation rate
              </p>
            </CardContent>
          </Card>

          {/* Median Time to Activation */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Timer className="w-5 h-5 text-orange-400" />
                <span className="text-xs text-zinc-500 uppercase">Time to Activate</span>
              </div>
              <div className="text-3xl font-bold text-white">
                {formatDuration(metrics?.median_activation_seconds)}
              </div>
              <p className="text-sm text-zinc-500 mt-1">
                Median time to first publish
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Trend Chart */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                Signup & Activation Trend
              </CardTitle>
              <CardDescription>Daily signups and activations over time</CardDescription>
            </CardHeader>
            <CardContent>
              <TrendChart 
                data={metrics?.trend_data || []}
                dataKey1="signups"
                dataKey2="activations"
                label1="Signups"
                label2="Activations"
              />
            </CardContent>
          </Card>

          {/* Time to Activation Histogram */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-400" />
                Time to First Publish
              </CardTitle>
              <CardDescription>Distribution of activation times</CardDescription>
            </CardHeader>
            <CardContent>
              {metrics?.histogram_buckets?.some(b => b.count > 0) ? (
                <HistogramChart buckets={metrics.histogram_buckets} />
              ) : (
                <div className="flex items-center justify-center h-40 text-zinc-500">
                  <p>No activation data available yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Funnel Visualization */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-green-400" />
              Activation Funnel
            </CardTitle>
            <CardDescription>User journey from signup to first publish</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center gap-4 py-6">
              {/* Stage 1: Signup */}
              <div className="text-center">
                <div className="w-32 h-24 bg-blue-500/20 border border-blue-500/40 rounded-lg flex flex-col items-center justify-center">
                  <Users className="w-6 h-6 text-blue-400 mb-1" />
                  <span className="text-xl font-bold text-white">{metrics?.signups || 0}</span>
                  <span className="text-xs text-zinc-400">Signups</span>
                </div>
                <span className="text-xs text-zinc-500 mt-2 block">100%</span>
              </div>

              {/* Arrow */}
              <div className="flex flex-col items-center text-zinc-600">
                <ArrowUpRight className="w-5 h-5 rotate-90" />
                <span className="text-xs mt-1">{formatPercent(metrics?.upload_rate)}</span>
              </div>

              {/* Stage 2: First Upload */}
              <div className="text-center">
                <div className="w-28 h-20 bg-purple-500/20 border border-purple-500/40 rounded-lg flex flex-col items-center justify-center">
                  <Upload className="w-5 h-5 text-purple-400 mb-1" />
                  <span className="text-lg font-bold text-white">{metrics?.users_with_first_upload || 0}</span>
                  <span className="text-xs text-zinc-400">Uploaded</span>
                </div>
                <span className="text-xs text-zinc-500 mt-2 block">{formatPercent(metrics?.upload_rate)}</span>
              </div>

              {/* Arrow */}
              <div className="flex flex-col items-center text-zinc-600">
                <ArrowUpRight className="w-5 h-5 rotate-90" />
                <span className="text-xs mt-1">{formatPercent(metrics?.activation_rate)}</span>
              </div>

              {/* Stage 3: Activated */}
              <div className="text-center">
                <div className="w-24 h-16 bg-green-500/20 border border-green-500/40 rounded-lg flex flex-col items-center justify-center">
                  <BookOpen className="w-5 h-5 text-green-400 mb-1" />
                  <span className="text-lg font-bold text-white">{metrics?.activated_creators || 0}</span>
                  <span className="text-xs text-zinc-400">Published</span>
                </div>
                <span className="text-xs text-zinc-500 mt-2 block">{formatPercent(metrics?.activation_rate)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/10">
                <Clock className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-zinc-400">Avg. Time to Activation</p>
                <p className="text-lg font-semibold text-white">
                  {formatDuration(metrics?.avg_activation_seconds)}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/10">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-zinc-400">Activation Rate</p>
                <p className="text-lg font-semibold text-white">
                  {formatPercent(metrics?.activation_rate)}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-500/10">
                <Upload className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-zinc-400">Upload Rate</p>
                <p className="text-lg font-semibold text-white">
                  {formatPercent(metrics?.upload_rate)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminCreatorActivation;
